import { useEffect, useState } from 'react';
import { formatDay, toISODate } from '@mononoke/shared';
import { AreaChip } from '../components/area-chip.js';
import { api, call } from '../platform/api.js';
import { navigate } from '../platform/router.js';
import { downloadReport } from '../platform/export.js';
import type { MaintenanceEntry, MaintenanceQueue } from '../../../api/src/catalog/maintenance.js';
import { t } from '../platform/i18n.js';
import { useLanguage } from '../platform/use-language.js';
import { NoteSheet } from '../components/note-sheet.js';
import { useMaxWidth } from '../components/use-max-width.js';
import { buildTree, flattenRows, type DisplayConfig, type EntityDisplaySpec } from '../display/engine.js';
import { DatasheetView, type ColumnDef } from '../display/datasheet.js';
import { GroupedList } from '../display/grouped-list.js';
import { CompactRow } from '../display/compact-row.js';
import { SelectBox, useCheckboxSelection } from '../components/select-box.js';
import { SelectionBackGuard } from '../components/selection-back-guard.js';
import { useConfirm } from '../components/confirm.js';
import { Sheet } from '../components/sheet.js';
import { Icon } from '../components/icons.js';

/**
 * The backlog's COST, not just its count (8c): how many garments carry an open job, and how
 * many of those cannot leave the building. "none can be checked out" is said only when it is
 * true of all of them — derived from `blocksUse` per row. This is the same derivation the
 * body carried inline before the flat-row redesign moved the summary up into the header band.
 */
/**
 * ONE ROW'S IDENTITY. A garment can carry two jobs (the multi-select really is two rows), and a
 * flag and an imported value can name the same work — so neither the item id nor the reason is
 * unique on its own. Hoisted because the render key, the selection set and the ordered spine the
 * drag walks must all say the SAME thing; three inline copies of this expression is how a
 * drag-select starts selecting a row nobody touched.
 */
function rowKey(entry: MaintenanceEntry): string {
  return `${entry.itemId}-${entry.source}-${entry.work}`;
}

/** The one and only view: grouped by job, garments A→Z within. Not a preference — no store, no bar. */
const MAINT_VIEW: DisplayConfig = { levels: [{ axis: 'work', orderMode: 'workflow', dir: 'asc' }], sortAxis: 'name', sortDir: 'asc' };

function backlogCost(entries: MaintenanceEntry[]): string {
  const garments = new Set(entries.map((e) => e.itemId)).size;
  const blocked = new Set(entries.filter((e) => e.blocksUse).map((e) => e.itemId)).size;
  const cost =
    blocked === 0
      ? 'all can still be checked out'
      : blocked === garments
        ? garments === 1
          ? 'it cannot be checked out'
          : 'none can be checked out'
        : `${String(blocked)} cannot be checked out`;
  return `${String(garments)} garment${garments === 1 ? '' : 's'} · ${cost}`;
}

/**
 * The maintenance queue — Phase 3 step 2. The list somebody works from while standing
 * at a rack with a steamer, so it is grouped BY THE JOB rather than by the item: you
 * do all the cleaning, then all the mending, and an item needing both appears under
 * each.
 *
 * TWO SOURCES, MARKED. Rows from Cheqroom's Maintenance column say so, and cannot be
 * cleared here — that field is authoritative there until the switch, and an edit made
 * here would be silently overwritten by the switch-day re-import. Rows raised as FLAGS
 * in this app can be resolved, because flags exist nowhere else. The screen says which
 * is which rather than offering an action that would quietly fail to stick.
 */
export function MaintenanceScreen(): JSX.Element {
  // Re-render when the language changes; `t` itself is a plain call (use-language.ts).
  useLanguage();
  const [queue, setQueue] = useState<MaintenanceQueue | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Open (the queue) vs Resolved (redesign 8c). Resolved is fetched lazily the first time it
   *  is opened, and its cache is dropped whenever a flag is resolved so it re-reads fresh. */
  const [view, setView] = useState<'open' | 'resolved'>('open');
  const [resolved, setResolved] = useState<MaintenanceEntry[] | null>(null);
  const [confirmEl, ask] = useConfirm();
  /** Whether the mass-acts sheet is open. */
  const [actsOpen, setActsOpen] = useState(false);
  /** The rows on screen, in the order they are drawn — the selection's spine. */
  const [rowKeys, setRowKeys] = useState<readonly { id: string }[]>([]);
  const pick = useCheckboxSelection(() => rowKeys);
  /** Signed thumb URLs keyed by object key (0.1.403) — the batch-sign every other list uses. */
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  /** Collapsed job rails — this visit only; the queue is a work list, not a view to keep. */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const phone = useMaxWidth(700);

  const refresh = async (): Promise<void> => {
    const result = await call('maintenanceQueue', () => api.maintenanceQueue.query());
    if (result.ok) setQueue(result.data);
    else setProblem(result.failure.message);
  };

  const loadResolved = async (): Promise<void> => {
    const result = await call('maintenanceResolved', () => api.maintenanceResolved.query());
    if (result.ok) setResolved(result.data);
    else setProblem(result.failure.message);
  };

  const showView = (next: 'open' | 'resolved'): void => {
    setView(next);
    setProblem(null);
    if (next === 'resolved' && resolved === null) void loadResolved();
  };

  useEffect(() => {
    void refresh();
  }, []);

  /**
   * SIGN THE THUMBS IN ONE BATCH, for whichever list is on screen (0.1.403). One request per
   * arrival of new keys, never one per row — the N+1 that made the cast cards crawl is the same
   * mistake at a different scale, and this queue can be long.
   *
   * Both views feed it: Open and Resolved are two lists of the same garments, and the key cache
   * is shared, so switching tabs re-signs nothing it already holds.
   */
  useEffect(() => {
    const shown = view === 'open' ? (queue?.entries ?? []) : (resolved ?? []);
    const wanted = [...new Set(shown.map((e) => e.photoKey).filter((k): k is string => k !== null && thumbs[k] === undefined))];
    if (wanted.length === 0) return;
    void (async () => {
      const signed = await call('photoUrls', () => api.photoUrls.query({ keys: wanted }));
      // A refused signing leaves the reserved tiles empty and says nothing: the queue is the
      // work list, and a missing picture must not bury the job it belongs to. The failure is
      // still classified and recorded by `call` (G10) — it is the SCREEN that stays quiet.
      if (signed.ok) setThumbs((prev) => ({ ...prev, ...signed.data.urls }));
    })();
  }, [queue, resolved, view]);

  /** The row whose resolve sheet is open — Close means cancel, exactly as the prompt's
   * null did (§4.1, 0.1.266: the sheet replaced `window.prompt`). */
  const [resolving, setResolving] = useState<MaintenanceEntry | null>(null);

  /**
   * ⚠️ A CHEQROOM-SOURCED JOB IS NOW CONFIRMED BEFORE IT IS CLEARED (0.1.410, reported:
   * *"The thumbs buttons need a confirmation message"*).
   *
   * It used to clear on the single tap. That was survivable while the control was a 120px button
   * reading "Mark repaired"; 0.1.409 turned it into a 44px icon sitting at the edge of a scrolling
   * list on a phone, which makes a mis-tap both easier and less obvious — and the act writes an
   * item edit that this screen cannot undo. Making the target smaller without asking what it now
   * needed was the defect; this is the missing half.
   *
   * A FLAG still goes through `NoteSheet` instead, and that is not an inconsistency: the note it
   * asks for is recorded on the garment's timeline, and it IS the confirmation step. A Cheqroom
   * value has nowhere to put a note, so offering a field that went nowhere would be the worse
   * answer (Guardrail 9's spirit — a control that pretends to record something).
   */
  const confirmField = async (entry: MaintenanceEntry): Promise<void> => {
    if (busy) return;
    const ok = await ask({
      title: `Mark this done on ${entry.itemName}?`,
      body: `“${entry.work}” is cleared off the garment, and that edit shows in the item’s own history. The garment itself is not changed. You can raise it again as a flag if it turns out not to be done.`,
      confirmLabel: t('maintenance.markDone'),
      tone: 'plain',
    });
    if (!ok) return;
    await resolve(entry, undefined);
  };

  const resolve = async (entry: MaintenanceEntry, note: string | undefined): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    const failed = await markDone(entry, note);
    if (failed === null) {
      // The resolved list now has one more row — drop its cache so it re-reads next view.
      setResolved(null);
      await refresh();
    } else setProblem(failed);
    setBusy(false);
  };

  /**
   * MARK ONE JOB DONE, whichever source it came from (0.1.405). A flag is resolved; an imported
   * Cheqroom value is cleared off the garment. Two different writes, ONE verb — the person at the
   * rack has done the same thing either way, and the queue used to make that distinction their
   * problem by offering the act on only half the rows.
   *
   * Returns a failure sentence, or null. The caller decides whether to say it one row at a time
   * or as a tally, so the mass path does not need a second copy of this.
   */
  const markDone = async (entry: MaintenanceEntry, note: string | undefined): Promise<string | null> => {
    if (entry.source === 'flag' && entry.flagId !== null) {
      const done = await call('resolveFlag', () =>
        api.resolveFlag.mutate({ flagId: entry.flagId!, itemId: entry.itemId, note }),
      );
      return done.ok ? null : done.failure.message;
    }
    const cleared = await call('clearMaintenanceValue', () =>
      api.clearMaintenanceValue.mutate({ itemId: entry.itemId, value: entry.work }),
    );
    if (!cleared.ok) return cleared.failure.message;
    // `cleared: false` means somebody else got there first. Not an error, and not a lie either —
    // the job IS done, so it is reported as done and the re-read below will show it gone (G10:
    // the distinction is kept, it simply does not warrant an alarm).
    return null;
  };

  /**
   * THE SELECTED ROWS, MARKED DONE IN ONE PASS — the mass act behind the long-press selection.
   *
   * ⚠️ ONLINE-ONLY AND NEVER QUEUED (G10's bulk rule). These are one call per row rather than one
   * bulk procedure, deliberately: the two sources need two different writes, and a partial result
   * has to be reportable as "N of M". A tally is the honest end of a loop that can half-succeed.
   */
  const markSelectedDone = async (): Promise<void> => {
    const chosen = openEntries.filter((e) => pick.selected.has(rowKey(e)));
    if (chosen.length === 0 || busy) return;
    const ok = await ask({
      title: `Mark ${String(chosen.length)} job${chosen.length === 1 ? '' : 's'} done?`,
      body:
        'Flags raised here are resolved; jobs imported from Cheqroom are cleared off the garment, ' +
        'and that edit shows in the item’s own history. The garments themselves are not changed.',
      confirmLabel: 'Mark done',
      tone: 'plain',
    });
    if (!ok) return;
    setBusy(true);
    setProblem(null);
    const failures: string[] = [];
    for (const entry of chosen) {
      const failed = await markDone(entry, undefined);
      if (failed !== null) failures.push(failed);
    }
    setResolved(null); // the Resolved list has new rows; drop its cache
    await refresh();
    pick.clear();
    setBusy(false);
    // Only a shortfall is said (0.1.523): the done jobs leaving the queue is the confirmation.
    if (failures.length > 0) {
      setProblem(
        `${String(failures.length)} of ${String(chosen.length)} did not go through — ${failures[0] ?? ''}`,
      );
    }
  };

  const [downloadNote, setDownloadNote] = useState<string | null>(null);
  const download = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setDownloadNote(null);
    const result = await downloadReport('maintenance');
    // The download's own sentence is KEPT (0.1.523): it states what the server sent, so a CSV that
    // never arrived cannot look like one that did (G10) — evidence, not a success line.
    if (result.ok) setDownloadNote(result.message);
    else setProblem(result.message);
    setBusy(false);
  };

  /**
   * ⚠️ THE SPINE IS THE DRAWN ORDER, NOT THE QUEUE ORDER. The board groups by reason, so the rows
   * appear in group order — and `gestureSelection` sweeps a RANGE between two indexes, which is
   * only the run under the finger if the indexes come from the same order the eye sees. Fed from
   * the same `tree` the sheet and the list render (`flattenRows`, the engine's drawn order), and
   * the row's `data-row-index` is this array's index — the drag hit-tests by that attribute.
   */
  useEffect(() => {
    setRowKeys(flattenRows(tree).map((e) => ({ id: rowKey(e) })));
    // `tree` is rebuilt every render from `queue`; the spine changes only when the queue does.
  }, [queue]);

  const thumbOf = (entry: MaintenanceEntry): string | undefined =>
    entry.photoKey === null ? undefined : thumbs[entry.photoKey];

  const openEntries = queue?.entries ?? [];
  const hasEntries = openEntries.length > 0;
  const backlog = hasEntries ? backlogCost(openEntries) : null;

  const chosen = openEntries.filter((e) => pick.selected.has(rowKey(e)));

  /**
   * THE JOB AXIS: one level, workflow order = the queue's own order of jobs (`queue.counts`, the
   * order the work gets done in). No tone — the categorical hue per job (ruled 8 Sep 2026), so
   * "cleaning" is one colour on every visit. The engine's empty label never shows: every entry
   * has a `work`.
   */
  const spec: EntityDisplaySpec<MaintenanceEntry> = {
    groupAxes: [
      {
        id: 'work',
        label: 'Job',
        keysOf: (e) => [e.work],
        workflow: (queue?.counts ?? []).map((c) => c.work),
        orderModes: ['workflow'],
        emptyLabel: 'No job',
      },
    ],
    sortAxes: [{ id: 'name', label: 'Garment', naturalDir: 'asc', compare: (a, b) => a.itemName.localeCompare(b.itemName) }],
  };
  const tree = buildTree(openEntries, spec, MAINT_VIEW);
  /** The drawn order — the selection's spine and every row's `data-row-index` (see the effect above). */
  const indexOf = new Map<string, number>();
  flattenRows(tree).forEach((e, i) => {
    if (!indexOf.has(rowKey(e))) indexOf.set(rowKey(e), i);
  });
  const toggle = (path: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  const provenance = (entry: MaintenanceEntry): string => {
    const when = entry.since === null ? '' : formatDay(toISODate(new Date(entry.since)));
    return entry.source === 'flag'
      ? `Flagged${entry.raisedBy === null ? '' : ` by ${entry.raisedBy}`}${when === '' ? '' : ` · ${when}`}`
      : t('maintenance.fromCheqroom');
  };
  /**
   * "Mark repaired", one 44px square per row at both widths (maintainer, 27 Aug 2026; kept on the
   * compact row 8 Sep). Its words live in `aria-label` / `title` — a screen reader moving down a
   * column of identical buttons needs the garment's name on each. `stopPropagation`: the row is
   * the door into the garment, and this button is not that door.
   */
  const markDoneButton = (entry: MaintenanceEntry): JSX.Element => (
    <button
      type="button"
      className="maint-done"
      disabled={busy}
      aria-label={`${t('maintenance.markDone')} — ${entry.itemName}`}
      title={t('maintenance.markDone')}
      onClick={(e) => {
        e.stopPropagation();
        if (entry.source === 'flag') setResolving(entry);
        else void confirmField(entry);
      }}
    >
      <Icon name="thumb" size={20} />
    </button>
  );
  /** The desk columns: Garment · Flagged by · Since · the act. The job is the rail, never a column. */
  const columns: ColumnDef<MaintenanceEntry>[] = [
    {
      id: 'garment',
      label: 'Garment',
      track: 'minmax(0, 2.2fr)',
      render: (entry) => (
        <span className="ds-lead">
          {thumbOf(entry) !== undefined ? (
            <img className="ds-thumb" src={thumbOf(entry)} alt="" loading="lazy" />
          ) : (
            <span className="ds-thumb" aria-hidden="true" />
          )}
          {/* A real link (middle-click, keyboard); the ROW's handler owns navigation. */}
          <a className="item-link ds-name" href={`/items/${entry.itemId}`} onClick={(e) => e.preventDefault()}>
            {entry.itemName}
          </a>
        </span>
      ),
    },
    {
      id: 'by',
      label: 'Flagged by',
      track: 'minmax(0, 1.2fr)',
      render: (entry) => (entry.source === 'flag' ? (entry.raisedBy ?? <span className="ds-muted">—</span>) : t('maintenance.fromCheqroom')),
    },
    {
      id: 'since',
      label: 'Since',
      track: 'minmax(0, 0.9fr)',
      render: (entry) => (entry.since === null ? <span className="ds-muted">—</span> : formatDay(toISODate(new Date(entry.since)))),
    },
    { id: 'act', label: '', track: 'minmax(0, 0.5fr)', render: (entry) => <span className="ds-row-act">{markDoneButton(entry)}</span> },
  ];

  return (
    <section>
      {/* THE PHONE'S BACK CLEARS THE TICKS (0.1.527; maintainer, 2 Oct 2026: "when I have items
          selected, make it so that my android back button clears my item selection, this should be
          app wide"). Mounted only while the queue has a selection. */}
      {pick.selected.size > 0 && <SelectionBackGuard onClear={() => pick.clear()} />}
      {confirmEl}
      <div className="screen-head board-head maint-head">
        <AreaChip area="maintenance">
          {/* THE BACKLOG COST beside the title (redesign 8c): how many garments, and how many
              cannot leave the building. Open view only — the Resolved list is history, not a
              cost. Moved here from the body, where it used to share a line with Download CSV. */}
          {view === 'open' && backlog !== null && <span className="screen-head-sub">{backlog}</span>}
        </AreaChip>
        <span className="detail-band-spacer" aria-hidden="true" />
        {/* ⚠️ THE CSV SITS BESIDE OPEN/RESOLVED, NOT FLOATING (maintainer, 27 Aug: "put the
            download CSV button in the same row to the right of the open and resolved"). It used
            to be a `.board-primary-act` float over the gesture-safe inset — which put a
            permanent pill over the list on a phone, and that inset is now wanted for the
            selection's own float. Two competing floats on one screen is what the project detail
            already had to untangle at 0.1.400.

            What has NOT changed: the export is never dropped because a frame moved it (G3/G10).
            Open view with rows only — an empty queue has nothing to download, and Resolved has
            its own list. */}
        {/* OPEN / RESOLVED (redesign 8c) — the same segmented control the other screens use. */}
        <span className="maint-head-acts">
        <span className="segmented" role="group" aria-label="Which jobs">
          <button
            type="button"
            className={view === 'open' ? 'segment segment-on' : 'segment'}
            aria-pressed={view === 'open'}
            onClick={() => showView('open')}
          >
            Open
          </button>
          <button
            type="button"
            className={view === 'resolved' ? 'segment segment-on' : 'segment'}
            aria-pressed={view === 'resolved'}
            onClick={() => showView('resolved')}
          >
            Resolved
          </button>
        </span>
        {view === 'open' && hasEntries && (
          <button type="button" className="chip maint-csv" disabled={busy} onClick={() => void download()}>
            Download CSV
          </button>
        )}
        </span>
      </div>
      {/* Everything that is not the sheet keeps a gutter inside the full-bleed pane (0.1.491). */}
      <div className="maint-inset">
      {downloadNote !== null && <p role="status">{downloadNote}</p>}
      {problem !== null && <p role="alert">{problem}</p>}

      {/* ─────────────── RESOLVED (8c) ─────────────── */}
      {view === 'resolved' && (
        <>
          {resolved === null && <p role="status">Loading…</p>}
          {resolved !== null && resolved.length === 0 && (
            <p>Nothing has been resolved yet. Flags you mark done from the Open list land here.</p>
          )}
          {resolved !== null && resolved.length > 0 && (
            <ul className="row-list">
              {resolved.map((entry) => (
                <li key={entry.flagId} className="maint-resolved-row">
                  {thumbOf(entry) !== undefined ? (
                    <img className="maint-thumb maint-thumb-sm" src={thumbOf(entry)} alt="" loading="lazy" />
                  ) : (
                    <span className="maint-thumb maint-thumb-sm" aria-hidden="true" />
                  )}
                  <a
                    href={`/items/${entry.itemId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate({ name: 'item', id: entry.itemId });
                    }}
                  >
                    {entry.itemName}
                  </a>{' '}
                  <small>
                    {entry.work}
                    {entry.resolvedAt !== null && ` · resolved ${formatDay(toISODate(new Date(entry.resolvedAt)))}`}
                    {entry.raisedBy !== null && ` · raised by ${entry.raisedBy}`}
                  </small>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {view === 'open' && queue === null && <p role="status">Loading…</p>}
      {/* The empty state carries the two facts that make the screen legible to somebody
          who has never opened it (redesign 8c): where a job comes from, and what an open
          one costs. The first sentence is the client's, verbatim. */}
      {view === 'open' && queue !== null && queue.entries.length === 0 && (
        <>
          <p>{t('maintenance.empty')}</p>
          <p>
            <small>
              A job appears here when a garment is flagged with a problem. A flag that blocks use also blocks
              check-out until it is resolved.
            </small>
          </p>
        </>
      )}
      </div>

      {/* GROUPED BY REASON (frame 2i): the by-reason accordions return — twenty-two identical
          "cleaning required" lines read as one group with a count, not twenty-two rows. This
          RE-GROUPS what 8c flattened; the final frame governs (redesign frames are final and
          supersede prior rulings). The reason lives ONCE, in the group header — our data carries
          a single `work` string per job (the flag's reason, or Cheqroom's field), and the frame's
          separate per-row description is not a field we hold (Guardrail 9), so the row is the
          garment and its provenance while the reason heads the group.

          ⚠️ The "flagged · flagged" copy bug the flat rows fixed stays fixed: the row shows the
          FLAG PROVENANCE ("Flagged by … · …"), never the item's status word — the garment is here
          because it is flagged, so restating the status was the doubling. */}
      {/* THE LIST IS THE ITEMS LIST'S DESIGN (0.1.491, maintainer 8 Sep 2026: "the design of the
          item list from the item tab applied to the item list in the maintenance tab"): the desk
          datasheet — rails per job with the categorical hue (ruled), 48px rows, the stripe on rows,
          the sheet as its own scroller — and the phone's 64px compact rows. The SAME components
          Items renders, fed a FIXED one-level grouping by job: no Display bar, no chips, no stored
          view (ruled: "I don't need a display function"). The groups are what they were — the
          queue's own order of jobs (frame 2i's by-reason accordions) — and the reason lives once,
          on the rail. The row's one act, "Mark repaired", stays on every row at both widths
          (ruled: keep the thumbs-up). */}
      {view === 'open' && hasEntries && !phone && (
        <div className="ds-wrap">
          <DatasheetView
            tree={tree}
            toneOf={() => undefined}
            columns={columns}
            rowKey={rowKey}
            edgeOf={() => 'var(--warn-edge)'}
            collapsed={collapsed}
            onToggle={toggle}
            onRowClick={(entry) => {
              // The click that trails a finished drag is not a tap on this row.
              if (pick.swallowClick()) return;
              navigate({ name: 'item', id: entry.itemId });
            }}
            rowClass={(entry) => (pick.selected.has(rowKey(entry)) ? 'ds-row-selected' : '')}
            rowIndexOf={(entry) => indexOf.get(rowKey(entry)) ?? 0}
            rowLead={(entry) => <SelectBox id={rowKey(entry)} label={`Select ${entry.itemName}`} index={indexOf.get(rowKey(entry)) ?? 0} pick={pick} />}
          />
        </div>
      )}
      {view === 'open' && hasEntries && phone && (
        <GroupedList
          tree={tree}
          rowKey={rowKey}
          collapsed={collapsed}
          onToggle={toggle}
          label="Maintenance queue"
          renderRow={(entry, index) => (
            <CompactRow
              name={entry.itemName}
              thumbUrl={thumbOf(entry)}
              sub={provenance(entry)}
              edge="var(--warn-edge)"
              index={index}
              href={`/items/${entry.itemId}`}
              className={pick.selected.has(rowKey(entry)) ? 'is-selected' : undefined}
              onClick={() => {
                if (pick.swallowClick()) return;
                navigate({ name: 'item', id: entry.itemId });
              }}
              lead={<SelectBox id={rowKey(entry)} label={`Select ${entry.itemName}`} index={index} pick={pick} />}
              trailing={markDoneButton(entry)}
            />
          )}
        />
      )}
      {/**
        * THE SELECTION'S FLOAT — the same pill the project and character screens use. It appears
        * as soon as anything is ticked and goes when nothing is: with the checkboxes always
        * present (0.1.406) there is no separate "selection mode" to be in, so the count IS the
        * state.
        */}
      {view === 'open' && chosen.length > 0 && (
        <>
          <div className="float-fade" aria-hidden="true" />
          <div className="float-stack">
            <button
              type="button"
              className="float-secondary items-float-actions"
              aria-label={`Actions (${String(chosen.length)})`}
              disabled={busy}
              onClick={() => setActsOpen(true)}
            >
              <span className="float-count">{chosen.length}</span> Actions
            </button>
          </div>
        </>
      )}
      {actsOpen && (
        <Sheet
          label={`Actions for ${String(chosen.length)} selected`}
          onClose={() => setActsOpen(false)}
          className="acts-sheet"
        >
          <p className="acts-sheet-head">
            <strong>
              {chosen.length} job{chosen.length === 1 ? '' : 's'} selected
            </strong>{' '}
            <button type="button" onClick={() => setActsOpen(false)}>
              Close
            </button>
          </p>
          <section className="item-acts-group">
            <p className="item-acts-row">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setActsOpen(false);
                  void markSelectedDone();
                }}
              >
                {t('maintenance.markDone')}
              </button>
            </p>
            <p className="item-acts-row">
              <button
                type="button"
                className="chip-clear"
                onClick={() => {
                  setActsOpen(false);
                  pick.clear();
                }}
              >
                Clear selection
              </button>
            </p>
          </section>
        </Sheet>
      )}
      {resolving !== null && (
        <NoteSheet
          title={`How was “${resolving.work}” resolved?`}
          fieldLabel="Note (optional)"
          confirmLabel="Resolve"
          busy={busy}
          onConfirm={(note) => {
            const entry = resolving;
            setResolving(null);
            void resolve(entry, note);
          }}
          onClose={() => setResolving(null)}
        />
      )}
    </section>
  );
}
